import { isResource } from '@medplum/core'
import type {
  CarePlan,
  CodeableConcept,
  Composition,
  CompositionSection,
  Patient,
  Practitioner,
  QuestionnaireResponse,
  Reference,
  Task,
} from '@medplum/fhirtypes'
import { addDays } from 'date-fns'
import {
  codeableConcept,
  codeFromCodeableConcept,
  displayFromCodeableConcept,
  hasCodeableConcept,
  keyByCodeableConcept,
} from '#src/codeableConcept.ts'
import {
  CARE_PLAN_SNAPSHOT_SECTION_TYPE_SYSTEM,
  CARE_PLAN_TYPE,
} from '#src/codeSystems.ts'
import { invariant } from '#src/invariant.ts'
import type { GoalCarePlan } from '#src/models/care-plans/GoalCarePlan.ts'
import { InitialCarePlan } from '#src/models/care-plans/InitialCarePlan.ts'
import type { Intervention } from '#src/models/care-plans/intervention.ts'
import { ExtendedComposition } from '#src/models/compositions/composition.ts'
import { CompositionCategory } from '#src/models/compositions/constants.ts'
import type { QuestionnaireWithResponse } from '#src/models/questionnaireResponse.ts'
import type { CarePlanParticipantSignatureTask } from '#src/models/tasks/signature/carePlanParticipantSignatureTask.ts'
import type { CarePlanSignatureTask } from '#src/models/tasks/signature/carePlanSignatureTask.ts'
import { createReferenceWithHistory } from '#src/references.ts'
import type { QueryTypes } from '#src/search.ts'
import { isOfType, now, recordValues } from '#src/utils.ts'

const SectionCodes = {
  InitialCarePlan: codeableConcept({
    system: CARE_PLAN_SNAPSHOT_SECTION_TYPE_SYSTEM,
    code: 'initial-care-plan',
    display: 'Initial Care Plan',
  }),
  GoalCarePlans: codeableConcept({
    system: CARE_PLAN_SNAPSHOT_SECTION_TYPE_SYSTEM,
    code: 'goal-care-plan',
    display: 'Goal Care Plan',
  }),
  Interventions: codeableConcept({
    system: CARE_PLAN_SNAPSHOT_SECTION_TYPE_SYSTEM,
    code: 'intervention',
    display: 'Intervention',
  }),
  Signatures: codeableConcept({
    system: CARE_PLAN_SNAPSHOT_SECTION_TYPE_SYSTEM,
    code: 'signature',
    display: 'Signature',
  }),
  ParticipantSignatures: codeableConcept({
    system: CARE_PLAN_SNAPSHOT_SECTION_TYPE_SYSTEM,
    code: 'participant-signature',
    display: 'Participant Signature',
  }),
  Demographics: codeableConcept({
    system: CARE_PLAN_SNAPSHOT_SECTION_TYPE_SYSTEM,
    code: 'demographics',
    display: 'Demographics',
  }),
} satisfies Record<string, CodeableConcept>

export class CarePlanSnapshot extends ExtendedComposition {
  static readonly RESOURCE_TYPE = 'Composition'

  static isInstance(value: unknown): value is Composition {
    return (
      isResource(value) &&
      isOfType(value, 'Composition') &&
      hasCodeableConcept(value.category, CompositionCategory.CarePlanSnapshot)
    )
  }

  constructor({
    date,
    author,
    initialCarePlan,
    goalCarePlans,
    interventions,
    signatures,
    participantSignatures,
    demographics,
  }: {
    date?: Date
    author: Reference<Practitioner>
    initialCarePlan: InitialCarePlan
    goalCarePlans: GoalCarePlan[]
    interventions: Intervention[]
    signatures: CarePlanSignatureTask[]
    participantSignatures: CarePlanParticipantSignatureTask[]
    demographics: QuestionnaireWithResponse[]
  }) {
    super({
      status: 'final',
      type: InitialCarePlan.TYPES[initialCarePlan.type],
      category: [CompositionCategory.CarePlanSnapshot],
      subject: initialCarePlan.patientSubject,
      date: date?.toISOString() ?? now(),
      author: [author],
      section: recordValues(SectionCodes).map((section) => ({
        code: section,
        mode: 'snapshot',
        entry: [],
      })),
      title: initialCarePlan.title,
    })
    this.initialCarePlan = createReferenceWithHistory(
      initialCarePlan.toFhirResource()
    )
    this.goalCarePlans = goalCarePlans.map((goalCarePlan) =>
      createReferenceWithHistory(goalCarePlan.toFhirResource())
    )
    this.interventions = interventions.map((intervention) =>
      createReferenceWithHistory(intervention.toFhirResource())
    )
    // Create references instead of versioned references
    this.signatures = signatures.map((signature) => signature.reference)
    this.participantSignatures = participantSignatures.map(
      (signature) => signature.reference
    )
    this.demographics = demographics
      .filter(
        (
          d
        ): d is QuestionnaireWithResponse & {
          questionnaireResponse: QuestionnaireResponse
        } => d.questionnaireResponse !== null
      )
      .map((demographics) =>
        createReferenceWithHistory(
          demographics.questionnaireResponse.toFhirResource()
        )
      )
  }

  get startDate(): Date {
    return new Date(this.resource.date)
  }

  get endDate(): Date {
    return addDays(this.startDate, 180)
  }

  get type(): keyof typeof InitialCarePlan.TYPES {
    return keyByCodeableConcept(InitialCarePlan.TYPES, this.resource.type)
  }

  get typeDisplay(): string {
    return displayFromCodeableConcept(this.resource.type, CARE_PLAN_TYPE) ?? ''
  }

  sectionByCode(code: CodeableConcept): CompositionSection {
    const section = super.sectionByCode(code)
    invariant(
      section,
      `Section with code ${codeFromCodeableConcept(code)} not found`
    )
    return section
  }

  get initialCarePlan(): Reference<CarePlan> {
    const section = this.sectionByCode(SectionCodes.InitialCarePlan)
    const reference = section.entry?.[0]
    invariant(reference, 'Initial care plan reference not found')
    return reference as Reference<CarePlan>
  }

  set initialCarePlan(reference: Reference<CarePlan>) {
    const section = this.sectionByCode(SectionCodes.InitialCarePlan)
    section.entry = [reference]
  }

  get goalCarePlans(): Reference<CarePlan>[] {
    const section = this.sectionByCode(SectionCodes.GoalCarePlans)
    return (section.entry ?? []) as Reference<CarePlan>[]
  }

  set goalCarePlans(references: Reference<CarePlan>[]) {
    const section = this.sectionByCode(SectionCodes.GoalCarePlans)
    section.entry = references
  }

  get interventions(): Reference<CarePlan>[] {
    const section = this.sectionByCode(SectionCodes.Interventions)
    return (section.entry ?? []) as Reference<CarePlan>[]
  }

  set interventions(references: Reference<CarePlan>[]) {
    const section = this.sectionByCode(SectionCodes.Interventions)
    section.entry = references
  }

  get signatures(): Reference<Task>[] {
    const section = this.sectionByCode(SectionCodes.Signatures)
    return (section.entry ?? []) as Reference<Task>[]
  }

  set signatures(references: Reference<Task>[]) {
    const section = this.sectionByCode(SectionCodes.Signatures)
    section.entry = references
  }

  get participantSignatures(): Reference<Task>[] {
    const section = this.sectionByCode(SectionCodes.ParticipantSignatures)
    return (section.entry ?? []) as Reference<Task>[]
  }

  set participantSignatures(references: Reference<Task>[]) {
    const section = this.sectionByCode(SectionCodes.ParticipantSignatures)
    section.entry = references
  }

  get demographics(): Reference<QuestionnaireResponse>[] {
    const section = this.sectionByCode(SectionCodes.Demographics)
    return (section.entry ?? []) as Reference<QuestionnaireResponse>[]
  }

  set demographics(references: Reference<QuestionnaireResponse>[]) {
    const section = this.sectionByCode(SectionCodes.Demographics)
    section.entry = references
  }

  static byInitialCarePlan({
    initialCarePlan,
  }: {
    initialCarePlan?: Reference<CarePlan>
  } = {}): QueryTypes {
    return {
      entry: initialCarePlan,
      category: CompositionCategory.CarePlanSnapshot,
    }
  }

  static byParticipant({
    participant,
  }: {
    participant: Reference<Patient>
  }): QueryTypes {
    return {
      patient: participant,
      type: `${CARE_PLAN_TYPE}|`,
      category: CompositionCategory.CarePlanSnapshot,
    }
  }
}
